import SEO from "../data/seo.json";

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_TITLE = "Cedar Views | Aluminium Windows and Doors";
const DEFAULT_DESC =
  "Cedar Views offer high quality windows and doors across Surrey and Hampshire. View our Inotherm Doors in our Chobham showroom.";

const normalise = (p) => (p.endsWith("/") ? p : `${p}/`);

// remote, admin-editable SEO overrides + redirect rules
let remote = { meta: {}, redirects: {} };
let loadPromise = null;

export const initSeo = () => {
  if (loadPromise) return loadPromise;
  try {
    const cached = JSON.parse(sessionStorage.getItem("cv-seo") || "null");
    if (cached && Date.now() - cached.t < 60000) remote = cached.d;
  } catch {}
  const fetchP = fetch(`${API}/api/seo/all`)
    .then((r) => r.json())
    .then((d) => {
      remote = d;
      try { sessionStorage.setItem("cv-seo", JSON.stringify({ t: Date.now(), d })); } catch {}
    })
    .catch(() => {});
  loadPromise = Object.keys(remote.meta || {}).length
    ? Promise.resolve()
    : Promise.race([fetchP, new Promise((res) => setTimeout(res, 1500))]);
  return loadPromise;
};

export const getRedirect = (path) => remote.redirects?.[normalise(path)] || null;

const mergedEntry = (key) => {
  const base = SEO[key];
  const over = remote.meta?.[key];
  if (!base && !over) return null;
  const m = { ...(base || {}) };
  if (over) {
    ["title", "description", "h1", "canonical"].forEach((k) => { if (over[k]) m[k] = over[k]; });
    if (over.noindex) m.noindex = true;
  }
  return m;
};

const findEntry = (paths) => {
  for (const p of paths) {
    if (!p) continue;
    const key = normalise(p);
    const entry = mergedEntry(key);
    if (entry) return { entry, key };
  }
  return { entry: null, key: normalise(paths[0] || "/") };
};

const setMeta = (attr, name, content) => {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!content) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
};

const canonicalHref = (canonical, key) => {
  if (!canonical) return window.location.origin + key;
  return canonical.startsWith("http") ? canonical : window.location.origin + normalise(canonical);
};

export const applySeo = ({ paths, fallbackTitle }) => {
  const { entry, key } = findEntry(paths);
  const title = entry?.title || fallbackTitle || DEFAULT_TITLE;
  const desc = entry?.description || DEFAULT_DESC;
  document.title = title;
  setMeta("name", "description", desc);
  setMeta("name", "keywords", entry?.keywords || "");
  setMeta("name", "robots", entry?.noindex ? "noindex" : "");
  setMeta("property", "og:title", entry?.ogTitle || title);
  setMeta("property", "og:description", entry?.ogDescription || desc);
  setCanonical(canonicalHref(entry?.canonical, key));
};

// custom SEO for dynamically generated pages (e.g. area pages)
export const applySeoCustom = ({ title, description, path, noindex }) => {
  const key = normalise(path || window.location.pathname);
  const over = remote.meta?.[key];
  const t = over?.title || title || DEFAULT_TITLE;
  const d = over?.description || description || DEFAULT_DESC;
  document.title = t;
  setMeta("name", "description", d);
  setMeta("name", "robots", over?.noindex || noindex ? "noindex" : "");
  setMeta("property", "og:title", t);
  setMeta("property", "og:description", d);
  setCanonical(canonicalHref(over?.canonical, key));
};

const clean = (t) => t.replace(/\s+/g, " ").trim();

const splitLines = (h1, fallback) => {
  if (!h1) return fallback;
  const text = clean(h1);
  if (text.length <= 16) return [text];
  const n = text.length > 42 ? 3 : 2;
  const budget = Math.ceil(text.length / n);
  const lines = [];
  let cur = "";
  text.split(" ").forEach((w) => {
    if (cur && (cur + " " + w).length > budget && lines.length < n - 1) {
      lines.push(cur);
      cur = w;
    } else {
      cur = cur ? cur + " " + w : w;
    }
  });
  if (cur) lines.push(cur);
  return lines;
};

// original live-site H1 split into display lines; falls back to designed lines
export const heroLines = (fallback, ...paths) => splitLines(findEntry(paths).entry?.h1, fallback);
